// 通用文件上传服务：支持将文件与可选文本一并上传
import { SessionManager } from './aiChatService';

export interface FileUploadResult {
    success: boolean;
    url?: string;
    error?: string;
    raw?: any;
    status?: number;
    message?: string;
}

export interface FileUploadResponseItem {
    fileName?: string;
    fileUrl?: string;
    fileId?: string;
    fileKey?: string;
    fileSize?: number;
    contentType?: string;
    message?: string;
    success?: boolean;
    error?: string | null;
    [key: string]: any;
}

export interface FileUploadBatchResult {
    success: boolean;
    status: number;
    message?: string;
    uploaded: FileUploadResponseItem[];
    failed: FileUploadResponseItem[];
    total: number;
    succeeded: number;
    failedCount: number;
    raw?: any;
}

class FileUploadService {
    private readonly FILE_UPLOAD_ENDPOINT = '/api/files/upload';

    async uploadFiles(
        files: File[],
        options?: {
            text?: string;
            transcription?: string;
            path?: string;
            sessionId?: string;
        }
    ): Promise<FileUploadBatchResult> {
        if (!files || files.length === 0) {
            throw new Error('请先选择要上传的文件');
        }

        try {
            const formData = new FormData();
            files.forEach(file => formData.append('files', file));

            // 单文件兼容字段
            if (files.length === 1) {
                formData.append('file', files[0]);
            }

            if (options?.text && options.text.trim()) {
                formData.append('text', options.text.trim());
                if (!options.transcription || !options.transcription.trim()) {
                    formData.append('transcription', options.text.trim());
                }
            }

            if (options?.transcription && options.transcription.trim()) {
                formData.append('transcription', options.transcription.trim());
            }

            if (options?.path) {
                formData.append('path', options.path);
            }

            // 添加sessionId
            const currentSessionId = options?.sessionId || SessionManager.getCurrentSessionId();
            if (currentSessionId) {
                formData.append('sessionId', currentSessionId);
                console.log('📁 文件上传包含sessionId:', currentSessionId);
            }

            const response = await fetch(this.FILE_UPLOAD_ENDPOINT, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            const status = response.status;
            const responseText = await response.text();
            let responseData: any = {};
            try {
                responseData = responseText ? JSON.parse(responseText) : {};
            } catch {
                responseData = {};
            }

            const uploaded = Array.isArray(responseData?.uploaded) ? responseData.uploaded : [];
            const failed = Array.isArray(responseData?.failed) ? responseData.failed : [];
            const succeeded = typeof responseData?.succeeded === 'number' ? responseData.succeeded : uploaded.length;
            const failedCount = typeof responseData?.failedCount === 'number' ? responseData.failedCount : failed.length;
            const total = typeof responseData?.total === 'number' ? responseData.total : succeeded + failedCount;
            const successFlag =
                typeof responseData?.success === 'boolean'
                    ? responseData.success
                    : (failedCount === 0 && status < 400);

            if (!response.ok) {
                return {
                    success: false,
                    status,
                    message: responseData?.message || responseText || `上传失败: ${status}`,
                    uploaded,
                    failed,
                    total,
                    succeeded,
                    failedCount,
                    raw: responseData
                };
            }

            return {
                success: successFlag,
                status,
                message: responseData?.message,
                uploaded,
                failed,
                total,
                succeeded,
                failedCount,
                raw: responseData
            };
        } catch (error) {
            console.error('文件上传失败:', error);
            return {
                success: false,
                status: 500,
                message: error instanceof Error ? error.message : '上传失败',
                uploaded: [],
                failed: [],
                total: 0,
                succeeded: 0,
                failedCount: 0,
                raw: null
            };
        }
    }

    async uploadFileWithText(file: File, text?: string, sessionId?: string): Promise<FileUploadResult> {
        const batchResult = await this.uploadFiles([file], { text, sessionId });

        if (batchResult.success && batchResult.uploaded.length > 0) {
            const uploaded = batchResult.uploaded[0];
            const url =
                uploaded?.fileUrl ||
                uploaded?.url ||
                uploaded?.path ||
                uploaded?.file_path ||
                batchResult.raw?.url ||
                batchResult.raw?.fileUrl ||
                undefined;

            return {
                success: true,
                url,
                raw: uploaded,
                status: batchResult.status,
                message: batchResult.message
            };
        }

        const failedItem = batchResult.failed[0];
        const errorMessage =
            failedItem?.message ||
            failedItem?.error ||
            batchResult.message ||
            '上传失败';

        return {
            success: false,
            error: errorMessage,
            raw: failedItem || batchResult.raw,
            status: batchResult.status,
            message: batchResult.message
        };
    }

    async uploadBlobWithText(blob: Blob, filename: string, text?: string, sessionId?: string): Promise<FileUploadResult> {
        const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
        return this.uploadFileWithText(file, text, sessionId);
    }
}

export const fileUploadService = new FileUploadService();
export default FileUploadService;
